export type TPreviewDateParts = {
  day: number;
  month: number;
  year: number;
};

function padPreviewDatePart(value: number): string {
  return `${value}`.padStart(2, '0');
}

function toEndOfDayUtcIso(year: number, month: number, day: number): string | null {
  const date = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function parsePreviewDateInput(value: string): TPreviewDateParts | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const slashMatch = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
  if (slashMatch) {
    return {
      day: Number.parseInt(slashMatch[1], 10),
      month: Number.parseInt(slashMatch[2], 10),
      year: Number.parseInt(slashMatch[3], 10),
    };
  }

  const dashMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (dashMatch) {
    return {
      day: Number.parseInt(dashMatch[3], 10),
      month: Number.parseInt(dashMatch[2], 10),
      year: Number.parseInt(dashMatch[1], 10),
    };
  }

  return null;
}

export function formatPreviewDate(value?: string | null): string {
  if (!value) {
    return '-';
  }

  const parsedInput = parsePreviewDateInput(value);
  if (parsedInput) {
    return `${padPreviewDatePart(parsedInput.day)}/${padPreviewDatePart(parsedInput.month)}/${parsedInput.year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return `${padPreviewDatePart(date.getUTCDate())}/${padPreviewDatePart(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
}

export function toPreviewDateInput(value?: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${padPreviewDatePart(date.getUTCDate())}/${padPreviewDatePart(date.getUTCMonth() + 1)}/${date.getUTCFullYear()}`;
}

export function fromPreviewDateInput(value: string): string | null {
  const parts = parsePreviewDateInput(value);
  if (!parts) {
    return null;
  }

  if (parts.year < 1000 || parts.month < 1 || parts.month > 12 || parts.day < 1 || parts.day > 31) {
    return null;
  }

  const resolved = toEndOfDayUtcIso(parts.year, parts.month, parts.day);
  if (!resolved) {
    return null;
  }

  const check = new Date(resolved);
  if (check.getUTCFullYear() !== parts.year || check.getUTCMonth() + 1 !== parts.month || check.getUTCDate() !== parts.day) {
    return null;
  }

  return resolved;
}

export function createDefaultPreviewDateInput(daysFromNow = 7): string {
  const today = new Date();
  const target = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate() + daysFromNow, 23, 59, 59, 999));
  return toPreviewDateInput(target.toISOString());
}
